import React, { useMemo, useState } from "react";
import { fullName, formatDate } from "../../lib/format";
import { Avatar } from "../shared/Avatar";
import { CustomIcon } from "../shared/CustomIcon";

// =============================================================
// MyAttendancePanel — shared personalised attendance panel used
// by both the /my-attendance page AND the "My Attendance" tab on
// the /attendance page. Renders 3 stat boxes (Rate / Present /
// Absent), a Day/Week/Month/Year period toggle, a chronological
// history list, and (for pastor/team_member) an "Assigned
// Members Attendance" section below.
// =============================================================

const PERIODS = [
  { id: "day",   label: "Day"   },
  { id: "week",  label: "Week"  },
  { id: "month", label: "Month" },
  { id: "year",  label: "Year"  },
];

function boundsFor(period) {
  const now = new Date();
  let start;
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1); // exclusive: end-of-today
  if (period === "day") {
    start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  } else if (period === "week") {
    // Monday-start current week
    const day = now.getDay();
    const daysSinceMon = (day + 6) % 7;
    start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysSinceMon);
  } else if (period === "year") {
    start = new Date(now.getFullYear(), 0, 1);
  } else {
    // month (default)
    start = new Date(now.getFullYear(), now.getMonth(), 1);
  }
  return { start, end };
}

export default function MyAttendancePanel({
  currentUser, members, attendance, services, events,
  isPastor, isTeamMember,
  navigate,
  defaultPeriod = "month",
  showBackButton = false,
}) {
  const [period, setPeriod] = useState(defaultPeriod);
  const { start, end } = useMemo(() => boundsFor(period), [period]);

  const sorted = useMemo(
    () => [...(attendance || [])].filter(a => !a.deletedAt).sort((a, b) => new Date(b.date) - new Date(a.date)),
    [attendance]
  );

  // Filter records to the selected period.
  const inPeriod = useMemo(() => {
    return sorted.filter(rec => {
      if (!rec.date) return false;
      const d = new Date(rec.date);
      return d >= start && d < end;
    });
  }, [sorted, start, end]);

  const notLinked = !currentUser?.memberId;

  // --- Personal stats for the selected period ---
  const myStats = useMemo(() => {
    if (notLinked) return { present: 0, absent: 0, total: 0, rate: 0 };
    let present = 0, absent = 0;
    inPeriod.forEach(rec => {
      const s = rec?.records?.[currentUser.memberId];
      if (s === "present") present++;
      else if (s === "absent") absent++;
    });
    const total = present + absent;
    const rate = total ? Math.round(present / total * 100) : 0;
    return { present, absent, total, rate };
  }, [inPeriod, currentUser, notLinked]);

  // --- Personal history rows for the selected period ---
  const myRows = useMemo(() => {
    if (notLinked) return [];
    return inPeriod
      .filter(rec => rec?.records && Object.prototype.hasOwnProperty.call(rec.records, currentUser.memberId))
      .map(rec => {
        const status = rec.records[currentUser.memberId];
        let label = "";
        if (rec.eventId) {
          const e = (events || []).find(x => x.id === rec.eventId);
          label = e?.name || "Event";
        } else {
          const s = (services || []).find(x => x.id === rec.serviceId);
          label = s?.name || "Service";
        }
        return { id: rec.id, date: rec.date, label, status };
      });
  }, [inPeriod, services, events, currentUser, notLinked]);

  // --- Assigned members summary for the selected period ---
  const assignedSummary = useMemo(() => {
    if (notLinked) return [];
    if (!(isPastor || isTeamMember)) return [];
    const ids = Array.isArray(currentUser?.assignedMembers) ? currentUser.assignedMembers : [];
    if (ids.length === 0) return [];
    return ids.map(mid => {
      const m = (members || []).find(x => x.id === mid);
      if (!m) return null;
      let present = 0, total = 0;
      inPeriod.forEach(rec => {
        const s = rec?.records?.[mid];
        if (s === "present") { present++; total++; }
        else if (s === "absent") { total++; }
      });
      const rate = total ? Math.round(present / total * 100) : null;
      return { id: mid, name: fullName(m), photo: m.photo, rate, present, total };
    }).filter(Boolean).sort((a, b) => (b.rate ?? -1) - (a.rate ?? -1));
  }, [inPeriod, members, currentUser, isPastor, isTeamMember, notLinked]);

  const box = (val, label, testid, color = "var(--kc-accent)") => (
    <div data-testid={testid} style={{
      flex: "1 1 0", minWidth: 0,
      background: "color-mix(in srgb, var(--kc-accent) 6%, transparent)",
      border: "1px solid color-mix(in srgb, var(--kc-accent) 25%, transparent)",
      borderRadius: 12,
      padding: "18px 10px",
      textAlign: "center",
    }}>
      <div style={{ fontFamily: "'Cinzel',serif", fontSize: 32, fontWeight: 700, color, lineHeight: 1 }}>{val}</div>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: "var(--kc-sub)", marginTop: 8 }}>{label}</div>
    </div>
  );

  if (notLinked) {
    return (
      <div data-testid="myatt-panel" style={{ padding: 20, textAlign: "center", background: "var(--kc-elevated)", borderRadius: 12, border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)" }}>
        <div style={{ marginBottom: 10 }}><CustomIcon name="clipboard" size={32} /></div>
        <div data-testid="myatt-nolink" style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 14, color: "var(--kc-sub)", lineHeight: 1.55 }}>
          No attendance record found. Ask your administrator to link your account to your member profile.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="myatt-panel">
      {showBackButton && navigate && (
        <button
          onClick={() => navigate(-1)}
          data-testid="myatt-back"
          style={{ background: "transparent", border: "1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent)", color: "var(--kc-accent)", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, marginBottom: 16 }}
        >← Back</button>
      )}

      {/* 3 stat boxes */}
      <div data-testid="myatt-stats" style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        {box(myStats.total > 0 ? `${myStats.rate}%` : "—", "Rate",    "myatt-rate")}
        {box(myStats.present,                                "Present", "myatt-present")}
        {box(myStats.absent,                                 "Absent",  "myatt-absent")}
      </div>

      {/* Period toggle */}
      <div data-testid="myatt-period-toggle" style={{ display: "flex", gap: 6, marginBottom: 16, background: "color-mix(in srgb, var(--kc-accent) 6%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 10, padding: 4, flexWrap: "wrap" }}>
        {PERIODS.map(p => (
          <button
            key={p.id}
            type="button"
            data-testid={`myatt-period-${p.id}`}
            onClick={() => setPeriod(p.id)}
            style={{
              flex: "1 1 60px", minWidth: 60,
              padding: "8px 10px",
              background: period === p.id ? "var(--kc-accent)" : "transparent",
              color: period === p.id ? "var(--kc-on-accent)" : "var(--kc-sub)",
              border: "none",
              borderRadius: 7,
              cursor: "pointer",
              fontFamily: "'Montserrat',sans-serif",
              fontSize: 12,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              transition: "background 0.15s, color 0.15s",
            }}
          >{p.label}</button>
        ))}
      </div>

      {/* Personal history list */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, letterSpacing: "0.10em", textTransform: "uppercase", color: "var(--kc-sub)", fontWeight: 700, marginBottom: 8 }}>
          My history · this {period}
        </div>
        {myRows.length === 0 ? (
          <div data-testid="myatt-empty" style={{ padding: 20, textAlign: "center", background: "var(--kc-elevated)", borderRadius: 10, border: "1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent)", color: "var(--kc-sub)", fontSize: 13, fontFamily: "'Montserrat',sans-serif" }}>
            No services or events attended this {period}.
          </div>
        ) : (
          <div data-testid="myatt-list" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {myRows.map(row => {
              const present = row.status === "present";
              return (
                <div key={row.id} data-testid={`myatt-row-${row.id}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, background: "var(--kc-elevated)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: "10px 14px" }}>
                  <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: "var(--kc-text)", fontWeight: 600, fontFamily: "'Inter',sans-serif" }}>{row.label}</div>
                    <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 2 }}>{formatDate(row.date)}</div>
                  </div>
                  <span style={{
                    fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                    padding: "5px 10px", borderRadius: 20,
                    background: present ? "rgba(74,222,128,0.15)" : "rgba(255,107,107,0.15)",
                    color: present ? "#4ade80" : "#ff6b6b",
                    border: `1px solid ${present ? "rgba(74,222,128,0.35)" : "rgba(255,107,107,0.35)"}`,
                  }}>
                    {present ? "Present" : (row.status || "Absent")}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Assigned members */}
      {(isPastor || isTeamMember) && (
        <div data-testid="myatt-assigned" style={{ borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent)", paddingTop: 16 }}>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 13, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--kc-accent)", fontWeight: 700, marginBottom: 10 }}>
            Assigned Members Attendance
          </div>
          {assignedSummary.length === 0 ? (
            <div data-testid="myatt-assigned-empty" style={{ fontSize: 12, color: "var(--kc-sub)", fontFamily: "'Montserrat',sans-serif" }}>
              No members are assigned to you yet.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {assignedSummary.map(a => (
                <div
                  key={a.id}
                  data-testid={`myatt-assigned-${a.id}`}
                  onClick={() => navigate && navigate(`/members/${a.id}`)}
                  role="link"
                  tabIndex={0}
                  onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && navigate) navigate(`/members/${a.id}`); }}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "6px 6px", borderRadius: 6, cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = "color-mix(in srgb, var(--kc-accent) 6%, transparent)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <Avatar name={a.name} photo={a.photo} memberId={a.memberId} style={{ width: 28, height: 28, fontSize: 11 }} />
                    <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: "var(--kc-accent)", textDecoration: "underline", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</span>
                  </div>
                  <span style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: 12, color: a.rate == null ? "var(--kc-meta)" : a.rate >= 70 ? "var(--kc-green)" : a.rate >= 40 ? "var(--kc-accent)" : "var(--kc-red)", whiteSpace: "nowrap" }}>
                    {a.rate == null ? "no data" : `${a.rate}% (${a.present}/${a.total})`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
